import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Backend} from '@common/backend'
import {Button, Link, LinkButton} from '@components/common/base/buttons'
import {Checkbox} from '@components/common/base/checkbox'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {Input} from '@components/common/base/input'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {menuItemHeight} from '@components/movement-templates/constants'
import {useBoard, useSyncEvent} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {BoardAppearanceContent} from './board-appearance-content'
import {
  BoardAppearanceContext,
  BoardAppearanceStore,
  useBoardAppearanceStore
} from './board-appearance-store'
import {TwTitlePrimary, TwTitleSecondary} from './containers'
import {TagItem} from './tag-item'
import {unsplashTags} from './unsplash/tags'
import {UnsplashColor} from './unsplash/types'
import {unsplashStore} from './unsplash/unsplash-store'
import {wrapUrlUtm} from './unsplash/wrap-url-utm'

const TwHeader = tw.div`
  flex
  items-center
  h-32
  mb-8
`

const TwContent = tw.div<{$disabled?: boolean}>`
  pt-8
  flex
  items-start
  flex-col
  h-[calc(100%-32px-8px-33px)]

  ${p => (p.$disabled ? 'opacity-30 pointer-events-none' : '')}
`

export const unsplashLink = 'https://unsplash.com/'

export const BoardAppearanceBase = observer(() => {
  const [state, store] = useBoardAppearanceStore()
  const tabBgEnabled = state.tabBgEnabled
  const searchOn = !!unsplashStore.isSearch

  useEffect(() => {
    store.syncNew()
  }, [store.boardId])

  useSyncEvent(
    'profile',
    {
      debounceTime: 50,
      filter: globals.currentUserId
    },
    () => {
      store.calculateCustomSkins()
    }
  )

  useEffect(() => {
    unsplashStore.resetSearch()
  }, [])

  const loadBg = (event?: React.MouseEvent) => {
    unsplashStore.resetSearch()
    if (event) {
      store.loadBg(event)
    }
  }

  const options = getOptions()
  const themeValue =
    options.find(o => o.key === unsplashStore.state.unsplashColor)?.text || ''

  const supported = !!Backend.features?.unsplash

  return (
    <div className="max-w-672 h-full">
      <TwHeader>
        <TwTitlePrimary>{Lang.msg.boardAppearance.title}</TwTitlePrimary>
      </TwHeader>

      <div className="py-8">
        <Checkbox
          value={tabBgEnabled}
          onChange={store.setTabBgEnabled}
          text={Lang.msg.boardAppearance.checkboxBg}
        />
      </div>

      <TwContent $disabled={!tabBgEnabled}>
        <div className="mb-16 flex items-center gap-16">
          <LinkButton onClick={loadBg} icon={'IconCustomPlus'} noPaddings>
            {Lang.msg.boardAppearance.uploadCustomBg}
          </LinkButton>
          {searchOn && (
            <LinkButton
              onClick={() => {
                unsplashStore.resetSearch()
                const scrollDiv = document.getElementById(
                  'board-appearance-scroll-id'
                )
                if (scrollDiv) {
                  scrollDiv.scrollTop = 0
                }
              }}
              noPaddings
            >
              {Lang.msg.boardAppearance.backToSaved}
            </LinkButton>
          )}
        </div>

        {supported && (
          <div className="flex items-center mb-8 gap-8">
            <Input
              value={unsplashStore.state.search}
              onChange={({text}) => unsplashStore.setSearch(text, 'board')}
              placeholder={Lang.msg.profilePage.unsplashPlaceholder}
              size="32"
              className="!w-440"
              onSubmit={() => unsplashStore.doSearch()}
            />
            <Button size="small" onClick={unsplashStore.doSearch}>
              {Lang.msg.profilePage.buttonSearch}
            </Button>
          </div>
        )}

        {supported && (
          <div className="flex gap-8 items-center mb-16">
            <TwTitleSecondary>
              {Lang.msg.boardAppearance.searchExamples}
            </TwTitleSecondary>
            {unsplashTags.map(tag => (
              <TagItem
                key={tag}
                text={tag}
                onClick={() => {
                  unsplashStore.setSearch(tag, 'board')
                  unsplashStore.doSearch()
                }}
              />
            ))}
          </div>
        )}

        <div className="flex flex-col gap-16 w-full h-[calc(100%-16px-16px-32px-8px-16px-16px)]">
          {!!unsplashStore.state.search && (
            <div className="flex gap-4">
              <TwTitlePrimary>{Lang.msg.boardAppearance.photos}</TwTitlePrimary>
              <Link style={{fontWeight: 600}} href={wrapUrlUtm(unsplashLink)}>
                {'Unsplash'}
              </Link>
            </div>
          )}
          {!!unsplashStore.state.search && (
            <div className="flex items-center gap-16 ">
              <TwTitleSecondary>
                {Lang.msg.boardAppearance.theme}
              </TwTitleSecondary>
              <div>
                <SelectWithDropdown
                  options={options}
                  value={themeValue}
                  size={menuItemHeight}
                  width={'min-w-230 max-w-230'}
                  onChange={val =>
                    unsplashStore.searchWithColor(val.key as UnsplashColor)
                  }
                />
              </div>
            </div>
          )}

          <BoardAppearanceContent />
        </div>
      </TwContent>
    </div>
  )
})

export const BoardAppearance = () => {
  const board = useBoard()
  const [store] = useState(() => new BoardAppearanceStore(board.id))

  return (
    <BoardAppearanceContext.Provider value={store}>
      <BoardAppearanceBase />
    </BoardAppearanceContext.Provider>
  )
}

export const getOptions = (): OptionsItem[] => {
  const lang = Lang.msg.boardAppearance

  return [
    {key: 'none', text: lang.themeNotDefined},

    {key: 'black_and_white', text: lang.themeBlackWhite},
    {key: 'black', text: lang.themeBlack},
    {key: 'white', text: lang.themeWhite},

    {key: 'red', text: lang.themeRed},
    {key: 'orange', text: lang.themeOrange},
    {key: 'yellow', text: lang.themeYellow},
    {key: 'green', text: lang.themeGreen},
    {key: 'blue', text: lang.themeBlue},
    {key: 'purple', text: lang.themePurple},
    {key: 'magenta', text: lang.themeMagenta},
    {key: 'teal', text: lang.themeTeal}
  ]
}
